import {
	Avatar,
	Box,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Typography,
} from '@mui/material';
import React from 'react';
import { DocumentNode, gql } from '@apollo/client';
import { Icon } from '../../../blocks/icons';
import MuiCheckbox from '../../../mui/MuiCheckbox';
import getInitials from '../../../lib/helpers/getInitials';
import { Spinner } from '../../Spinner';
import ContactCustomerAvatar from '../../ContactCustomerAvatar';
import rpcShared from '@rockpapercoin/rpc-shared';
import {
	DocumentSettingsModalShareListContactFragment,
	DocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment,
	DocumentOwnerType,
	useDocumentSettingsModalShareList_GetOrganizationsForUserQuery,
} from '#types';
import { getDocumentOwnerName } from '../../../lib/helpers';
import styles from './DocumentSettingsModalShareList.module.scss';
import {
	useDocumentSettingsModalShareList,
	useDocumentSettingsModalShareListContactFragment,
	useDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment,
	useDocumentSettingsModalShareListOrganizationFragment,
	UseDocumentSettingsModalShareListProps,
} from './useDocumentSettingsModalShareList';

const documentSettingsModalShareListContactFragment = gql`
	fragment documentSettingsModalShareListContact on Contact {
		...useDocumentSettingsModalShareListContact
	}
	${ useDocumentSettingsModalShareListContactFragment }
`;

const documentSettingsModalShareListCustomDocumentWithDownloadUrlFragment = gql`
	fragment documentSettingsModalShareListCustomDocumentWithDownloadUrl on Document {
		...useDocumentSettingsModalShareListCustomDocumentWithDownloadUrl
	}
	${ useDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment }
`;

const documentSettingsModalShareListOrganizationFragment = gql`
	fragment documentSettingsModalShareListOrganization on Organization {
		id
		...useDocumentSettingsModalShareListOrganization
		contacts(where: $contactWhere) {
			...documentSettingsModalShareListContact
		}
	}
	${ useDocumentSettingsModalShareListOrganizationFragment }
	${ documentSettingsModalShareListContactFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DocumentSettingsModalShareList_getOrganizationsForUser = gql`
	query DocumentSettingsModalShareList_getOrganizationsForUser(
		$where: UserByIdUniqueInput!
		$take: Int
		$contactWhere: ContactWhereInput
	) {
		getOrganizationsForUser(where: $where, take: $take) {
			organizations {
				...documentSettingsModalShareListOrganization
			}
		}
	}
	${ documentSettingsModalShareListOrganizationFragment }
`;

export type DocumentSettingsModalShareListProps = {
	document: DocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment;
	customerId?: string;
	setSharedWithContacts: React.Dispatch<
	React.SetStateAction<DocumentSettingsModalShareListContactFragment[]>
	>;
	sharedWithContacts: DocumentSettingsModalShareListContactFragment[];
	setSharedWithCustomer: React.Dispatch<React.SetStateAction<boolean>>;
	sharedWithCustomer: boolean;
	isOwner: boolean;
};

type DocumentSettingsModalShareListPropsWithFragment =
	React.FC<DocumentSettingsModalShareListProps> & {
		fragments: {
			documentSettingsModalShareListCustomDocumentWithDownloadUrlFragment: DocumentNode;
			documentSettingsModalShareListContactFragment: DocumentNode;
		};
	};

export const DocumentSettingsModalShareList: DocumentSettingsModalShareListPropsWithFragment =
	( { customerId, document, isOwner, ...props } ) => {
		const {
			visible,
			organizationsToDisplay,
			documentIsShared,
			toggleShareWithOrganization,
			getUsername,
			loading,
			contactBetweenCustomerAndActingVendor,
			nonCustomerOrgUser,
		} = useDocumentSettingsModalShareList( {
			useDocumentSettingsModalShareList_GetOrganizationsForUserQuery,
			customerId,
			document,
			setSharedWithContacts:
				props.setSharedWithContacts as UseDocumentSettingsModalShareListProps['setSharedWithContacts'],
			sharedWithContacts:
				props.sharedWithContacts as UseDocumentSettingsModalShareListProps['sharedWithContacts'],
			setSharedWithCustomer: props.setSharedWithCustomer,
			sharedWithCustomer: props.sharedWithCustomer,
			isOwner,
		} );

		const getShareListTitle = () => {
			if ( isOwner ) {
				if ( nonCustomerOrgUser ) {
					return 'Who should have access to this document';
				}
				if ( !nonCustomerOrgUser && organizationsToDisplay.length > 0 ) {
					return 'Do you want to share this document with any of your vendors?';
				}
				if ( !nonCustomerOrgUser && organizationsToDisplay.length === 0 ) {
					return 'Once you connect with a planner or vendors, you will be able to share this document with them.';
				}
			}
			if ( documentIsShared ) {
				if ( nonCustomerOrgUser ) {
					return `${ getDocumentOwnerName(
						document
					) } has shared this document with the following people:`;
				} else {
					return 'Once you connect with a planner or vendors, you will be able to share this document with them.';
				}
			}
			return null;
		};

		if ( !visible ) {
			return null;
		}

		return (
			<>
				<Typography variant='h6'>{ getShareListTitle() }</Typography>
				{ isOwner ? (
					loading ? (
						<Box className={ styles.spinner }>
							<Spinner />
						</Box>
					) : (
						<List role='list'>
							{ document.ownerType === DocumentOwnerType.Organization ? (
								<ListItem
									role='listitem'
									onClick={ () =>
										props.setSharedWithCustomer( ( prevState ) => !prevState )
									}
									data-cy={ contactBetweenCustomerAndActingVendor?.customer.id }
									secondaryAction={
										<MuiCheckbox
											checked={ props.sharedWithCustomer }
											// click bubbles up - since we have onClick on the list item, it would be triggered twice
										/>
									}
								>
									<ListItemAvatar>
										{ contactBetweenCustomerAndActingVendor ? (
											<ContactCustomerAvatar
												contact={ contactBetweenCustomerAndActingVendor }
											/>
										) : (
											<Avatar className={ styles.avatar }>
												{ getInitials(
													document.customer?.clientUser ||
														document.customer?.orgUser
												) }
											</Avatar>
										) }
									</ListItemAvatar>
									<ListItemText
										className={ styles.listText }
										primary={
											contactBetweenCustomerAndActingVendor
												? rpcShared.contacts.getCustomerName(
													contactBetweenCustomerAndActingVendor
												  ).fullName
												: getUsername( document.customer )
										}
									/>
								</ListItem>
							) : null }
							{ organizationsToDisplay.map( ( organizationContact ) => (
								<ListItem
									key={ organizationContact.id }
									role='listitem'
									onClick={ () =>
										toggleShareWithOrganization( organizationContact )
									}
									data-cy={ organizationContact.vendor.id }
									secondaryAction={
										<MuiCheckbox
											checked={
												!!props.sharedWithContacts.find(
													( item ) => item.id === organizationContact.id
												)
											}
											// click bubbles up - since we have onClick on the list item, it would be triggered twice
										/>
									}
								>
									<ListItemAvatar>
										<Avatar
											src={
												organizationContact.vendor.image
													? `${ organizationContact.vendor.image }?updatedAt=${ organizationContact.vendor.updatedAt }`
													: undefined
											}
											className={ styles.avatar }
										>
											<Icon
												type='organization'
												className={ styles.defaultIcon }
											/>
										</Avatar>
									</ListItemAvatar>
									<ListItemText
										className={ styles.listText }
										primary={ organizationContact.vendor.name }
									/>
								</ListItem>
							) ) }
						</List>
					)
				) : null }
			</>
		);
	};

DocumentSettingsModalShareList.fragments = {
	documentSettingsModalShareListCustomDocumentWithDownloadUrlFragment,
	documentSettingsModalShareListContactFragment,
};
